import { Injectable } from '@angular/core';
import moment from 'moment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { first } from 'rxjs/operators';
import { MessageService } from './message.service';
import { AbstractControl } from '@angular/forms';
import {
  AutocompleteDropdownsInterface,
  LocationType,
  MessagePopup,
  RemoveEventParamsInterface,
} from '@core/models';
import { GameService } from './game.service';
import { FirebaseService } from '@core/services/firebase.service';
import { CalendarApiService } from '@core/api-services/calendar-api.service';

@Injectable({
  providedIn: 'root',
})
export class CalendarService {
  constructor(
    private _messageService: MessageService,
    private _gameService: GameService,
    private _fireBase: FirebaseService,
    private _calendarApiService: CalendarApiService,
  ) {}
  public date: BehaviorSubject<moment.Moment> = new BehaviorSubject(moment());
  public eventTypeFilters: BehaviorSubject<string[]> = new BehaviorSubject([
    'specialty',
    'regular',
    'private',
    'demo',
  ]);
  public locationTypes: LocationType[] = [];
  modalOpened: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  changeMonth(dir: number): void {
    const value = this.date.value.add(dir, 'month');
    this.date.next(value);
  }

  openConfirmModal<T>(data: MessagePopup): Observable<T> {
    return this._messageService
      .openConfirmModal(data)
      .afterClosed()
      .pipe(first());
  }

  checkRecurringOnDelete({
    eventData,
    eventId,
    code,
    deleteFn,
    closeFn,
  }: RemoveEventParamsInterface): void {
    const modalData: MessagePopup = {
      msg: 'Do you want to delete only this event?',
      isConfirm: true,
      showCloseIcon: true,
      confirmBtnInfo: {
        accept: {
          text: 'Delete only this event',
          returnValue: { close: false, allEvents: false },
        },
        reject: {
          text: 'Delete all recurring events',
          returnValue: { close: false, allEvents: true },
        },
      },
    };
    if (eventData?.recurring_event && !eventData?.is_last_event) {
      this.openConfirmModal<{ close: boolean; allEvents: boolean }>(
        modalData,
      ).subscribe(({ close, allEvents }) => {
        if (close) {
          closeFn();
          return;
        }
        this.deleteEvent(eventId, deleteFn, code, allEvents);
      });
    } else {
      this.deleteEvent(eventId, deleteFn, code);
    }
  }

  onClickDeleteEvent(deleteParams: RemoveEventParamsInterface): void {
    const messageData = {
      msg: `Are you sure you want to delete the event?`,
      sub_msg: '',
      isConfirm: true,
    };

    this.openConfirmModal<boolean>(messageData).subscribe(
      (response: boolean) => {
        if (response) {
          this.checkRecurringOnDelete(deleteParams);
        }
      },
    );
  }

  private async deleteEvent(
    id: string,
    subsFn,
    code: number,
    allEvents: boolean = false,
  ): Promise<void> {
    try {
      await this._fireBase.deleteGameFromFirebase(code, id);
    } catch (err) {
      console.error('Failed to delete game from Firebase:', err);
    }

    if (this._gameService.isActiveEventId(id)) {
      this._gameService.cleanStorage();
    }

    this._calendarApiService.deleteEvent(id, allEvents).subscribe(() => {
      subsFn();
    });
  }

  checkAutocompleteDropdowns(dropdowns: AutocompleteDropdownsInterface): void {
    const { host, location, game } = dropdowns.dropdownControls;
    const { hosts, locations, games } = dropdowns.dropdownLists;
    const controls: {
      formControl: AbstractControl;
      checkBy1: string;
      checkBy2: string;
      checkIn: any[];
    }[] = [
      {
        formControl: host,
        checkBy1: 'username',
        checkBy2: 'user_id',
        checkIn: hosts,
      },
      {
        formControl: location,
        checkBy2: 'location_name',
        checkBy1: 'location_id',
        checkIn: locations,
      },
    ];

    if (dropdowns.showGameSelect) {
      controls.push({
        formControl: game,
        checkBy1: 'game_title',
        checkBy2: 'game_id',
        checkIn: games,
      });
    }
    for (const control of controls) {
      if (typeof control.formControl.value === 'string') {
        const selectedItem = control.checkIn.find(
          (hostForm) =>
            hostForm[control.checkBy1] === control.formControl.value ||
            hostForm[control.checkBy2] === control.formControl.value,
        );
        if (selectedItem) {
          control.formControl.setErrors(null);
          control.formControl.setValue(selectedItem);
          continue;
        }

        control.formControl.setErrors({ wrongInput: true });
      }
    }
  }
}
